import { AxiosError } from 'axios';
import { CandidateType } from 'components/Slate/Candidates/Candidates';
import { FORM_ERROR } from 'final-form';
import { useAppSelector } from 'helpers/hooks';
import { getNewPaymentsById, getPaymentsData } from 'selectors/payments';
import { getRefValue } from 'services/storage';
import { PaymentsData } from 'store/payments/types';
import { IUserData } from 'store/user/types';
import {
  OathRecurringInterval,
  PayRequestData,
  PayResponseData,
  PaymentMethod,
  PaymentMethodType,
  StripePaymentMethod,
  StripeRecurringInterval,
} from 'store/pay/types';
import { ENV_CONFIG } from 'config/environment';
import {
  DEFAULT_CLIENT_ID,
  getIsRetiredOrUnemployed,
  getUrlParams,
  isKeyInObject,
  roundNumberToHundredths,
} from 'helpers/utils';
import { IPage } from 'store/donatePage/types';
import { useField } from 'react-final-form';
import { getUserState } from 'selectors/user';
import { IFilter } from 'store/filters/types';
import { TicketWithQuantitySelected } from 'store/eventPage/types';
import { ExpressCheckout } from 'store/expressCheckout/types';

const DE_STAGING_ACTIVE_RECIPIENTS = [
  'Oath API927410000',
  'Oath API927610000',
  'Oath API927710000',
  'Oath API927810000',
];

const getRecipientId = (candidate: CandidateType, index: number) => {
  if (ENV_CONFIG().MODE === 'PRODUCTION') {
    return candidate.de_id;
  }
  // If not production, use one of four active recipients in DE staging
  const onesDigit = parseInt(index.toString().split('').pop() as string);
  if (onesDigit < 2) {
    return DE_STAGING_ACTIVE_RECIPIENTS[0];
  }
  if (onesDigit < 4) {
    return DE_STAGING_ACTIVE_RECIPIENTS[1];
  }
  if (onesDigit < 8) {
    return DE_STAGING_ACTIVE_RECIPIENTS[2];
  }
  return DE_STAGING_ACTIVE_RECIPIENTS[3];
};

const getOathTippingId = () => {
  return ENV_CONFIG().DEMOCRACY_ENGINE.OATH_TIP_RECIPIENT_ID;
};

export interface InitialValuesInterface extends IUserData {
  payment_type: string;
  default_token: string;
  tip: number;
}

const isPaymentCreditCard = (payment: PaymentsData) =>
  payment.payment_authorization_display_name.includes('Credit Card');

export const usePaymentOptions = () => {
  const tokenField = useField('default_token').input.value;
  const { newPaymentsById, payments, userState } = useAppSelector(state => ({
    newPaymentsById: getNewPaymentsById(state),
    payments: getPaymentsData(state),
    userState: getUserState(state),
  }));

  const ccPayments: PaymentsData[] = [];
  const achPayments: PaymentsData[] = [];

  if (payments) {
    payments.forEach(payment => {
      if (isPaymentCreditCard(payment)) {
        ccPayments.push(payment);
      } else {
        achPayments.push(payment);
      }
    });
  }

  if (
    !userState.data &&
    tokenField &&
    tokenField !== 'cc_token' &&
    tokenField !== 'cc_token' &&
    isKeyInObject(tokenField, newPaymentsById)
  ) {
    const savedPayment = newPaymentsById[tokenField];
    if (isPaymentCreditCard(savedPayment)) {
      ccPayments.push(savedPayment);
    } else {
      achPayments.push(savedPayment);
    }
  }

  return { ccPayments, achPayments };
};

export const getCheckoutInitialValues = (
  defaultTipAmount: number,
  userData?: IUserData | ExpressCheckout | null,
  isPledge = false
) => {
  return {
    first_name: userData?.first_name || '',
    last_name: userData?.last_name || '',
    address1: userData?.address1 || '',
    address2: userData?.address2 || '',
    city: userData?.city || '',
    state: userData?.state || '',
    zipcode: userData?.zipcode || '',
    employer: userData?.employer || '',
    employer_address1: userData?.employer_address1 || '',
    employer_address2: userData?.employer_address2 || '',
    employer_city: userData?.employer_city || '',
    employer_country: userData?.employer_country || '',
    employer_state: userData?.employer_state || '',
    employer_zipcode: userData?.employer_zipcode || '',
    occupation: userData?.occupation || '',
    email: userData?.email || '',
    payment_type: isPledge ? 'pledge' : 'stripe',
    tip: isPledge ? 0 : defaultTipAmount,
  };
};

export const getInitialValuesFromApiData = (
  userApiData: IUserData | undefined | object = {},
  paymentsApiData: PaymentsData[] | [] = [],
  defaultTipAmount: number
) => {
  const defaultPayment = paymentsApiData && paymentsApiData[0];
  return {
    ...userApiData,
    payment_type: defaultPayment && !isPaymentCreditCard(defaultPayment) ? 'ach' : 'credit_card',
    default_token: defaultPayment && defaultPayment.payment_authorization_token,
    tip: defaultTipAmount,
  };
};

export const getUpdatedUser = (user: IUserData, values: any) =>
  Object.keys(user).reduce((updatedUser, key) => {
    if (isKeyInObject(key, values) && user[key] !== values[key] && (user[key] || values[key])) {
      updatedUser[key] = values[key];
    }
    return updatedUser;
  }, {});

const getPaymentTokenFromLastPayment = (
  payResponseData: PayResponseData,
  paymentsApiData: PaymentsData[] | [] = [],
  newPaymentsById?: {
    [token: string]: PaymentsData;
  }
) => {
  if (
    paymentsApiData?.find(
      payment => payment.payment_authorization_token === payResponseData.token
    ) ||
    isKeyInObject(payResponseData.token || '', newPaymentsById || {})
  ) {
    return payResponseData.token;
  }
  if (!paymentsApiData?.length) {
    return null;
  }
  return payResponseData.payment_method === 'credit_card' ? 'cc_token' : 'ach_token';
};

export const getInitialValuesFromLastPayment = (
  payResponseData: PayResponseData,
  paymentsApiData: PaymentsData[] | [] = [],
  defaultTipAmount: number,
  userApiData?: IUserData,
  newPaymentsById?: {
    [token: string]: PaymentsData;
  }
) => {
  const prevTipAmountStr = payResponseData.line_items.find(
    item => item.recipient_id === getOathTippingId()
  )?.amount;
  let tipAmount = defaultTipAmount;
  if (prevTipAmountStr) {
    tipAmount = roundNumberToHundredths(parseFloat(prevTipAmountStr.replaceAll('$', '')));
  }
  return {
    first_name: payResponseData.donor_first_name,
    last_name: payResponseData.donor_last_name,
    address1: payResponseData.donor_address1,
    address2: payResponseData.donor_address2,
    city: payResponseData.donor_city,
    state: payResponseData.donor_state,
    zipcode: payResponseData.donor_zip,
    employer: payResponseData.compliance_employer,
    employer_address1:
      payResponseData.compliance_employer_address1 || userApiData?.employer_address1,
    employer_address2:
      payResponseData.compliance_employer_address2 || userApiData?.employer_address2,
    employer_city: payResponseData.compliance_employer_city || userApiData?.employer_city,
    employer_country: payResponseData.compliance_employer_country || userApiData?.employer_country,
    employer_state: payResponseData.compliance_employer_state || userApiData?.employer_state,
    employer_zipcode: payResponseData.compliance_employer_zip || userApiData?.employer_zipcode,
    occupation: payResponseData.compliance_occupation,
    email: payResponseData.donor_email,
    payment_type: payResponseData.payment_method,
    default_token: getPaymentTokenFromLastPayment(
      payResponseData,
      paymentsApiData,
      newPaymentsById
    ),
    tip: tipAmount,
  };
};

const getPartnerAsRefCode = (filters: IFilter[]) => {
  const { urlPartner, urlTags } = getUrlParams();
  if (urlPartner) {
    return urlPartner;
  } else if (urlTags?.length) {
    const tagsQueryStrs = filters
      .filter(filter => urlTags.includes(filter.filter_id))
      .map(filter => filter.filter_name.replaceAll(' ', ''));
    return `search-${tagsQueryStrs.join('-')}`;
  } else {
    return '';
  }
};

export const getStripeDescription = (candidates: CandidateType[], hasTip: boolean) => {
  const maxDescriptionLength = 1000;
  const tipSuffix = hasTip ? ' (+ tip)' : '';
  const longDescriptionSuffix = `, and more${tipSuffix}`;
  if (candidates.length === 1) {
    return `${candidates[0].recipient_name}${tipSuffix}`;
  }
  const description = `${candidates
    .map(candidate => candidate.recipient_name)
    .join(candidates.length > 2 ? ', ' : ' and ')}`;
  if (description.length + tipSuffix.length < maxDescriptionLength) {
    return `${description}${tipSuffix}`;
  }
  const trimmedDescription = description
    .slice(0, maxDescriptionLength - longDescriptionSuffix.length)
    .split(', ')
    .slice(0, -1)
    .join(', ');
  return `${trimmedDescription}${longDescriptionSuffix}`;
};

export const getPaymentMethodTypeDisplay = (selectedPaymentMethodType?: PaymentMethodType) => {
  if (selectedPaymentMethodType === 'wire') {
    return 'wire';
  } else if (selectedPaymentMethodType === 'grant') {
    return 'grant';
  } else if (selectedPaymentMethodType === 'check') {
    return 'check';
  } else {
    return 'donation';
  }
};

export const getOfflineReviewHelperText = (selectedPaymentMethodType?: PaymentMethodType) => {
  if (selectedPaymentMethodType === 'wire') {
    return 'Click below to securely access wire instructions and complete your wire transfer through your bank.';
  }
  return `Click confirm below to confirm your donation. We will send you an email with ${getPaymentMethodTypeDisplay(
    selectedPaymentMethodType
  )} instructions.`;
};

export const getOfflineSubmitButtonText = (selectedPaymentMethodType?: PaymentMethodType) => {
  if (selectedPaymentMethodType === 'wire') {
    return 'View wire instructions';
  }
  return `Confirm ${getPaymentMethodTypeDisplay(selectedPaymentMethodType)} pledge`;
};

export const getPaymentMethodFromStripe = (
  selectedPaymentMethod?: string,
  selectedPaymentMethodType: PaymentMethodType = 'wire'
): 'credit_card' | 'ach' | PaymentMethodType => {
  if (selectedPaymentMethod === StripePaymentMethod.CARD) {
    return 'credit_card';
  } else if (selectedPaymentMethod === StripePaymentMethod.US_BANK_ACCOUNT) {
    return 'ach';
  } else if (selectedPaymentMethod === 'pledge') {
    return selectedPaymentMethodType;
  }
  return 'credit_card';
};

export const oathRecurringIntervalFromStr = (interval: string) => {
  const validIntervals: OathRecurringInterval[] = ['day', 'week', 'month', 'quarter', 'year'];
  return validIntervals.includes(interval as OathRecurringInterval)
    ? (interval as OathRecurringInterval)
    : 'month';
};

export const stripeRecurringIntervalFromOath = (interval: OathRecurringInterval) => {
  const validIntervals: StripeRecurringInterval[] = ['day', 'week', 'month', 'year'];
  return validIntervals.includes(interval as StripeRecurringInterval)
    ? (interval as StripeRecurringInterval)
    : 'month';
};

export const stripeRecurringIntervalCountFromOath = (interval: OathRecurringInterval) => {
  if (interval === 'quarter') {
    return 3;
  }
  return 1;
};

export const getDisplayTextFromRecurringInterval = (
  interval: OathRecurringInterval | StripeRecurringInterval,
  isCapitalized?: boolean
) => {
  let displayText = `${interval}ly`;
  if (interval === 'day') {
    displayText = 'daily';
  }
  if (isCapitalized) {
    displayText = displayText[0].toUpperCase() + displayText.slice(1);
  }
  return displayText;
};

export const getPayRequestBodyStripe = ({
  formValues,
  candidates,
  clientId,
  clientName,
  pageId,
  pageName,
  pageQueryString,
  selectedPaymentMethod,
  selectedPaymentMethodType,
  ticketSelections,
}: {
  formValues: InitialValuesInterface;
  candidates: CandidateType[];
  pageId?: number;
  pageName?: string;
  pageQueryString?: string;
  clientId?: number;
  clientName?: string;
  selectedPaymentMethod?: string;
  selectedPaymentMethodType?: PaymentMethodType;
  ticketSelections?: TicketWithQuantitySelected[];
}) => {
  const { urlRef } = getUrlParams();
  const requestBody = {
    line_items: candidates.map((candidate: CandidateType) => ({
      amount: Math.round((candidate.donationAmount || 0) * 100),
      recipient_id:
        selectedPaymentMethod === 'pledge' ? candidate.recipient_id : candidate.stripe_id,
      recipient_name: candidate.recipient_name,
    })),
    donor_last_name: formValues.last_name,
    donor_first_name: formValues.first_name,
    donor_zip: formValues.zipcode,
    donor_address1: formValues.address1,
    donor_address2: formValues.address2,
    donor_city: formValues.city,
    donor_state: formValues.state,
    donor_country_code: formValues.country,
    source_code: pageQueryString || '',
    ref_code: urlRef || getRefValue() || '',
    donor_email: formValues.email,
    compliance_employer: formValues.employer,
    compliance_occupation: formValues.occupation,
    cc_last_name: formValues.last_name,
    cc_first_name: formValues.first_name,
    payment_method: getPaymentMethodFromStripe(selectedPaymentMethod, selectedPaymentMethodType),
    client_id: clientId || DEFAULT_CLIENT_ID,
    page_id: pageId || null,
    page_name: pageName || null,
    statement_descriptor_suffix: clientName || null,
    description: getStripeDescription(candidates, !!Number(formValues.tip)),
  } as PayRequestData;

  if (Number(formValues.tip)) {
    requestBody.line_items.push({
      amount: Math.round(formValues.tip * 100),
      recipient_id: 'oath_stripe',
      recipient_name: 'Oath / 51C Collaborative, Inc.',
    });
  }

  if (ticketSelections?.length) {
    requestBody.event_ticket_purchases = ticketSelections.map(ticket => ({
      event_ticket_id: ticket.id,
      quantity: ticket.quantity_selected,
    }));
  }

  if (getIsEmployerAddressRequired(candidates, formValues.employer, formValues.occupation)) {
    requestBody.compliance_employer_address1 = formValues.employer_address1;
    requestBody.compliance_employer_address2 = formValues.employer_address2;
    requestBody.compliance_employer_city = formValues.employer_city;
    requestBody.compliance_employer_state = formValues.employer_state;
    requestBody.compliance_employer_zip = formValues.employer_zipcode;
    requestBody.compliance_employer_country = formValues.employer_country;
  }

  return requestBody;
};

export const getPayRequestBody = (
  formValues,
  candidates: CandidateType[],
  page: IPage | undefined,
  filters: IFilter[],
  isMonthly = false,
  payTokenId: string | null,
  selectedTickets?: TicketWithQuantitySelected[]
) => {
  const requestBody = {
    payment_authorization_token: formValues.default_token,
    payment_token_id: payTokenId,
    line_items: candidates.map((candidate, index) => ({
      amount: Math.round((candidate.donationAmount || 0) * 100),
      recipient_id: getRecipientId(candidate, index),
    })),
    donor_last_name: formValues.last_name,
    donor_first_name: formValues.first_name,
    donor_zip: formValues.zipcode,
    donor_address1: formValues.address1,
    donor_address2: formValues.address2,
    donor_city: formValues.city,
    donor_state: formValues.state,
    donor_country_code: formValues.country,
    source_code: getUrlParams().urlRef || getRefValue() || '',
    ref_code: getPartnerAsRefCode(filters),
    donor_email: formValues.email,
    compliance_employer: formValues.employer,
    compliance_occupation: formValues.occupation,
    cc_last_name: formValues.last_name,
    cc_first_name: formValues.first_name,
    payment_method: formValues.payment_type,
    client_id: page?.client_id || DEFAULT_CLIENT_ID,
    page_id: page?.id || null,
  } as PayRequestData;

  if (Number(formValues.tip)) {
    requestBody.line_items.push({
      amount: Math.round(formValues.tip * 100),
      recipient_id: getOathTippingId(),
    });
  }

  if (selectedTickets?.length) {
    requestBody.event_ticket_purchases = selectedTickets.map(ticket => ({
      event_ticket_id: ticket.id,
      quantity: ticket.quantity_selected,
    }));
  }

  if (getIsEmployerAddressRequired(candidates, formValues.employer, formValues.occupation)) {
    requestBody.compliance_employer_address1 = formValues.employer_address1;
    requestBody.compliance_employer_address2 = formValues.employer_address2;
    requestBody.compliance_employer_city = formValues.employer_city;
    requestBody.compliance_employer_state = formValues.employer_state;
    requestBody.compliance_employer_zip = formValues.employer_zipcode;
    requestBody.compliance_employer_country = formValues.employer_country;
  }

  if (isMonthly) {
    requestBody.subscription_request = {
      type: 'monthly',
      ...(getUrlParams().urlPartner !== 'monthly-infra' && { expires_after: '20241231' }),
    };
  }

  return requestBody;
};

export const getCcExpObj = (expDate: string) => ({
  month: expDate.split('/')[0],
  year: `20${expDate.split('/')[1].slice(-2)}`,
});

export const getDEPayRequestBody = formValues => {
  const requestBody = {
    donation: {
      gid: '',
      donor_first_name: formValues.first_name,
      donor_last_name: formValues.last_name,
      donor_address1: formValues.address1,
      donor_city: formValues.city,
      donor_state: formValues.state,
      donor_zip: formValues.zipcode,
      donor_country_code: formValues.country,
      donor_email: formValues.email,
      payment_method: formValues.payment_type,
      ...(formValues.payment_type === 'credit_card' && {
        cc_number: formValues.cc_number,
        cc_month: getCcExpObj(formValues.cc_exp).month,
        cc_year: getCcExpObj(formValues.cc_exp).year,
        cc_verification_value: formValues.cc_verification_value,
      }),
      ...(formValues.payment_type === 'ach' && {
        ach_account_type: formValues.ach_account_type,
        ach_routing_number: formValues.ach_routing_number,
        ach_account_number: formValues.ach_account_number,
      }),
      cc_first_name: formValues.first_name,
      cc_last_name: formValues.last_name,
      authtest_request: true,
      line_items: null,
      token_request: true,
    },
  };

  return requestBody;
};

export const getTipFromPayResponse = (payResponse: PayResponseData) => {
  const tipItem = payResponse.line_items.find(item => item.recipient_id === getOathTippingId());
  if (!tipItem) {
    return 0;
  }
  return Number(tipItem.amount.replace('$', ''));
};

export const getDonationTotalFromPayResponse = (payResponse: PayResponseData) =>
  payResponse.line_items.reduce(
    (total, item) => total + Number(item.amount.replaceAll(/[$,]/g, '')),
    0
  );

export const getFormErrors = (err: AxiosError) => {
  try {
    // @ts-ignore:next-line
    let errorMsg = err.response?.data?.detail?.map(detailItem => detailItem[1]).join('. ');
    if (errorMsg) {
      // @ts-ignore:next-line
      errorMsg = `${err.response?.data?.error}. ${errorMsg}`;
    } else {
      errorMsg = err.response?.data || 'Donation failed.';
    }

    return { [FORM_ERROR]: errorMsg };
  } catch {
    return { [FORM_ERROR]: err.response?.data || 'Donation failed' };
  }
};

// Commenting out while we use statsig experiment webhook for tip options
// See site/src/hooks/statsig.ts useExperimentTipAmountOptions
// const defaultTips = [{ percentage: 0.05 }, { percentage: 0.1 }, { percentage: 0.15 }];
// const highDollarTips = [{ percentage: 0.01 }, { percentage: 0.03 }, { percentage: 0.05 }];
// const lowDollarTips = [
//   { amount: 1, percentage: null },
//   { amount: 3, percentage: null },
//   { amount: 5, percentage: null },
// ];
// const highDollarTipsVariant2 = [
//   { amount: 100, percentage: null },
//   { amount: 200, percentage: null },
//   { amount: 300, percentage: null },
// ];

export const roundTwoDigits = (num: number) => Math.round(num * 100) / 100;

// Commenting out while we use statsig experiment webhook for tip options
// See site/src/hooks/statsig.ts useDefaultTipAmount
// export const getTipOptions = (donationAmount: number, isVariant2: boolean) => {
//   let tips = defaultTips;
//   if (donationAmount < 20) {
//     return lowDollarTips;
//   }
//   if (donationAmount >= 10000 && isVariant2) {
//     return highDollarTipsVariant2;
//   }
//   if (donationAmount > 5000) {
//     tips = highDollarTips;
//   }

//   return tips.map(tip => ({
//     percentage: tip.percentage,
//     amount: roundTwoDigits(tip.percentage * donationAmount),
//   }));
// };

// export const getDefaultTipAmount = (donationAmount: number, isTippingVariant2: boolean) => {
//   return getTipOptions(donationAmount, isTippingVariant2)[1].amount;
// };

export const canSkipPersonalDetails = (
  initialValues: InitialValuesInterface,
  candidates: CandidateType[]
) => {
  const {
    email,
    first_name: firstName,
    last_name: lastName,
    address1: address,
    city: city,
    state,
    zipcode,
    employer,
    occupation,
    employer_address1: employerAddress1,
    employer_city: employerCity,
    employer_state: employerState,
    employer_zipcode: employerZip,
  } = initialValues;
  return (
    email &&
    firstName &&
    lastName &&
    address &&
    city &&
    state &&
    zipcode &&
    employer &&
    occupation &&
    (!getIsEmployerAddressRequired(candidates, employer, occupation) ||
      (employerAddress1 && employerCity && employerState && employerZip))
  );
};

export const getInitialActivePageIndex = (
  initialValues: InitialValuesInterface,
  candidates: CandidateType[]
) => {
  let initialActivePageIndex = 0;
  const { payment_type: paymentType, default_token: paymentToken } = initialValues;

  if (canSkipPersonalDetails(initialValues, candidates)) {
    initialActivePageIndex = 1;
  }
  if (
    paymentToken &&
    paymentToken !== 'cc_token' &&
    paymentToken !== 'ach_token' &&
    paymentType &&
    initialActivePageIndex === 1
  ) {
    initialActivePageIndex = 2;
  }
  return initialActivePageIndex;
};

export const getProgressBarWidthStripe = (activePageIndex: number) => {
  if (activePageIndex === 0) {
    return '67%';
  }
  return '100%';
};

export const getProgressBarWidth = (activePageIndex: number) => {
  if (activePageIndex === 0) {
    return '50%';
  }
  if (activePageIndex === 1) {
    return '75%';
  }
  return '100%';
};

export const getIsEmployerAddressRequiredByCandidates = (candidates: CandidateType[]) => {
  return !!candidates.find(candidate => candidate.race?.election.is_employer_address_required);
};

export const getIsEmployerAddressRequired = (
  candidates: CandidateType[],
  employer: string,
  occupation: string
) => {
  return (
    getIsEmployerAddressRequiredByCandidates(candidates) &&
    !getIsRetiredOrUnemployed(employer, occupation)
  );
};

// payment utils

export const getValidateFn =
  (validate: (value: any) => void | string, error?: string) => (value: any) => {
    if (error) {
      return error;
    }
    return validate(value);
  };

export const getIsCcNumberAmex = (ccNumber: string) =>
  ccNumber.startsWith('34') || ccNumber.startsWith('37') || /^[*]{11}[0-9]{4}$/.test(ccNumber);

export const validateCcNumber = (ccNumber: any = '') => {
  const ccRegex = new RegExp(`^[0-9]{${getIsCcNumberAmex(ccNumber) ? 15 : 16}}$`);
  const isValid = ccRegex.test(ccNumber) || /^[*]{11,12}[0-9]{4}$/.test(ccNumber);
  return isValid ? undefined : 'Credit card number invalid';
};

export const validateCcExp = (exp: any = '') => {
  const [month = '', year = ''] = exp.split('/');
  const isValid = /^[0-9]{2}$/.test(month) && /^[0-9]{2,4}$/.test(year) && year.length !== 3;
  return isValid ? undefined : 'Invalid';
};

export const validateCVV = (cvv: any = '') => {
  const isValid = /^[0-9]{3}$/.test(cvv) || /^[*]{3}$/.test(cvv);
  return isValid ? undefined : 'CVV invalid';
};

export const validateCVVAmex = (cvv: any = '') => {
  const isValid = /^[0-9]{4}$/.test(cvv) || /^[*]{4}$/.test(cvv);
  return isValid ? undefined : 'CVV invalid';
};

export const validateRoutingNumber = (routingNumber: any = '') => {
  const isValid = /^[0-9]{9}$/.test(routingNumber) || /^[*]{9}$/.test(routingNumber);
  return isValid ? undefined : 'Please enter a valid routing number';
};

export const validateAccountNumber = (accountNumber: any = '') => {
  const isValid = /^[0-9]{5,17}$/.test(accountNumber) || /^[*]{12}[0-9]{4}$/.test(accountNumber);
  return isValid ? undefined : 'Please enter a valid account number';
};

export const formatCcExpiration = (value: any = '') => {
  if (value.length === 3 && value[2] !== '/') {
    return `${value.slice(0, 2)}/${value.slice(-1)}`;
  }
  return value;
};

export const getNewCcToken = (
  token: string,
  paymentType: PaymentMethod,
  ccPayments: PaymentsData[]
) => {
  if (
    paymentType === 'ach' ||
    ccPayments.find(payment => payment.payment_authorization_token === token)
  ) {
    return 'cc_token';
  }
  return token;
};

export const getNewAchToken = (
  token: string,
  paymentType: PaymentMethod,
  achPayments: PaymentsData[]
) => {
  if (
    paymentType === 'credit_card' ||
    achPayments.find(payment => payment.payment_authorization_token === token)
  ) {
    return 'ach_token';
  }
  return token;
};

export const paymentErrorsDefaultCC = {
  cc_number: undefined as undefined | string,
  cc_exp: undefined as undefined | string,
  cc_verification_value: undefined as undefined | string,
  ccForm: undefined as undefined | string,
};

export const paymentErrorsDefaultAch = {
  ach: undefined as undefined | string,
  achForm: undefined as undefined | string,
};

export const paymentErrorsDefault = {
  ...paymentErrorsDefaultCC,
  ...paymentErrorsDefaultAch,
};

export const paymentErrorTypes = {
  'Year is not a valid year': 'cc_exp',
  'Card number is not a valid credit card number': 'cc_number',
  'Card number is not recognized as a valid brand': 'cc_number',
  'Year card is expired': 'cc_exp',
  'It is possible you have entered an invalid ba': 'ach',
};

export const getPaymentErrorFieldName = (error: string) => {
  if (Object.keys(paymentErrorTypes).includes(error)) {
    return paymentErrorTypes[error];
  }
  return '';
};

export const getIsDataValid = (hasValidationErrors: boolean, errors = {}) => {
  return (
    !hasValidationErrors ||
    (Object.keys(errors).length === 1 && isKeyInObject('default_token', errors))
  );
};

export const getIsCheckoutMonthly = () => {
  const { urlPartner } = getUrlParams();
  const regex = /^monthly-/;
  return !!urlPartner && regex.test(urlPartner);
};
